/**
 * Time helpers shared across the redesigned timeline.
 *
 * Windows are expressed in "minutes since local midnight" (0–1440).
 * Slots carry `startTime` / `endTime` as "HH:MM" strings in local time, so
 * all of our math stays in the integer-minutes space — no Date objects
 * needed once the window is set.
 */

export const DAY_MINUTES = 24 * 60;

export const timeToMinutes = (t) => {
  if (!t) return 0;
  const [h = 0, m = 0] = String(t).split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

export const formatMinutes = (mins, { shortAmpm = false } = {}) => {
  const normalised = ((mins % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  const h = Math.floor(normalised / 60);
  const m = normalised % 60;
  const hh = h % 12 || 12;
  const ampm = h < 12 ? (shortAmpm ? 'am' : 'AM') : (shortAmpm ? 'pm' : 'PM');
  return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
};

export const formatHourLabel = (h) => {
  if (h === 0 || h === 24) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
};

export const formatDuration = (mins) => {
  const total = Math.max(0, Math.round(mins));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

/**
 * Countdown "Xm Ys" — used for the On Air Now "ends in" label.
 * Scales by magnitude so a multi-day campaign reads "6 days, 4 hours"
 * instead of "148h 23m", while sub-minute shows seconds for live feel.
 */
export const formatCountdown = (millis) => {
  const total = Math.max(0, Math.floor(millis / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days >= 1) {
    // >= 1 day out: coarse "X days, Y hours" — minutes are noise at this scale.
    return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  if (hours >= 1) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
};

/**
 * Is the schedule document active on the given ISO date (YYYY-MM-DD)?
 * When dateStr is omitted, defaults to today's local date.
 *
 * Recurrence is implicit in [startDate, endDate] — a schedule plays every
 * day in that range. Legacy daysOfWeek / days_of_week fields are IGNORED
 * (deprecated 2026-04-23 — a week is a week, no weekday filtering).
 */
export const isScheduleForDate = (s, dateStr) => {
  const target = dateStr || new Date().toISOString().slice(0, 10);
  if (s.date) {
    const d = new Date(s.date).toISOString().slice(0, 10);
    return d === target;
  }
  if (s.startDate && s.endDate) {
    const sd = new Date(s.startDate).toISOString().slice(0, 10);
    const ed = new Date(s.endDate).toISOString().slice(0, 10);
    return target >= sd && target <= ed;
  }
  if (s.startDate) {
    return target >= new Date(s.startDate).toISOString().slice(0, 10);
  }
  // No date bounds means a daily recurring slot — counts as any date.
  return true;
};

// Back-compat alias — existing callers import this name.
export const isScheduleForToday = (s) => isScheduleForDate(s);

/**
 * Project a minute-of-day onto a fractional position [0,1] within a window.
 * Values outside the window are clipped at the edges. Callers multiply by the
 * live viewport width (or use it directly as a percentage for CSS left/width)
 * — this keeps the layout responsive without a ResizeObserver round-trip.
 */
export const minutesToFraction = (mins, win) => {
  const { startMin, endMin } = win;
  const span = Math.max(1, endMin - startMin);
  const clamped = Math.max(startMin, Math.min(endMin, mins));
  return (clamped - startMin) / span;
};

export const minutesToPx = (mins, win, width) => minutesToFraction(mins, win) * width;

/**
 * Fractional range of a slot inside the window, clipped to the window edges.
 * Returns `{ leftPct, widthPct, clipLeft, clipRight }` with 0–1 fractions so
 * the caller can plug them straight into CSS percentage values.
 */
export const slotRange = (slotStart, slotEnd, win) => {
  const { startMin, endMin } = win;
  const visStart = Math.max(slotStart, startMin);
  const visEnd = Math.min(slotEnd, endMin);
  if (visEnd <= visStart) {
    return { leftPct: 0, widthPct: 0, clipLeft: false, clipRight: false };
  }
  const span = Math.max(1, endMin - startMin);
  return {
    leftPct: (visStart - startMin) / span,
    widthPct: (visEnd - visStart) / span,
    clipLeft: slotStart < startMin,
    clipRight: slotEnd > endMin,
  };
};

export const nowMinutesOfDay = (d = new Date()) => d.getHours() * 60 + d.getMinutes();
